/**
 * PortOne V2 결제 연동 유틸
 *
 * 플로우: prepare(서버) → PortOne.requestPayment(SDK) → confirm(서버)
 *
 * 1. paymentService.prepare({ reservation_id, payment_type })
 *    → 서버는 V2 pre-register 호출 후 { order_id(=paymentId), amount, order_name } 반환
 *
 * 2. PortOne.requestPayment({ storeId, channelKey, paymentId, ... })
 *    → V2 SDK가 결제창 띄움. 응답은 Promise (V1처럼 callback 아님)
 *    → 성공: { txId, paymentId, ... }, 실패: { code, message }
 *
 * 3. paymentService.confirm({ payment_key=txId, order_id=paymentId, amount })
 *    → 서버가 V2 GET /payments/{paymentId}로 검증 후 Payment 레코드 생성
 */

import config from '../config';
import { paymentService } from './services';
import { getCurrentBeLang } from '../contexts/LanguageContext';

/**
 * UI 언어 → V2 SDK locale 매핑.
 * V2는 'KO_KR' / 'EN_US' / 'JA_JP' / 'ZH_CN' 등 지원. 미지원 언어는 EN_US 폴백.
 */
function resolveLocale() {
  const ui = getCurrentBeLang();
  if (ui === 'ko') return 'KO_KR';
  if (ui === 'ja') return 'JA_JP';
  if (ui === 'zh') return 'ZH_CN';
  return 'EN_US';
}

function getPortOne() {
  if (typeof window !== 'undefined' && window.PortOne) {
    return window.PortOne;
  }
  return null;
}

/** PortOne V2 SDK + Store ID 준비 여부 */
export function isPortOneReady() {
  return !!(config.PORTONE_V2_STORE_ID && getPortOne());
}

// 간편결제 결제수단 ID → PortOne V2 easyPayProvider 매핑.
//   INICIS V2 에서 EASY_PAY 를 easyPayProvider 없이(통합창) 호출하면 checkout-service
//   prepare/v2 가 400 Bad Request 를 반환 → 간편결제사를 항상 명시 호출한다.
//   provider 는 easyPay: { easyPayProvider } 형태로 SDK 에 전달(최상위 아님).
const EASY_PAY_PROVIDERS = {
  simple_kakao: 'KAKAOPAY',
  simple_naver: 'NAVERPAY',
  simple_toss: 'TOSSPAY',
  simple_apple: 'APPLEPAY',
  simple_samsung: 'SAMSUNGPAY',
};

/**
 * 프론트 UI 결제수단 ID → V2 SDK 파라미터 매핑.
 * V2의 payMethod: 'CARD' | 'VIRTUAL_ACCOUNT' | 'TRANSFER' | 'MOBILE' | 'EASY_PAY' | 'PAYPAL'
 */
function resolveChannel(paymentMethod) {
  // 간편결제 5종 — 카드와 동일한 KG이니시스 채널, EASY_PAY + 간편결제사 명시.
  if (EASY_PAY_PROVIDERS[paymentMethod]) {
    return {
      channelKey: config.PORTONE_CHANNEL_KEY,
      payMethod: 'EASY_PAY',
      easyPayProvider: EASY_PAY_PROVIDERS[paymentMethod],
    };
  }
  switch (paymentMethod) {
    case 'paypal':
      return {
        channelKey: config.PORTONE_PAYPAL_CHANNEL_KEY,
        payMethod: 'PAYPAL',
        currency: 'USD',
      };
    case 'bank':
      return {
        channelKey: config.PORTONE_CHANNEL_KEY,
        payMethod: 'VIRTUAL_ACCOUNT',
      };
    case 'simple':
      // 구버전 통합창 폴백(현재 UI 미노출). provider 없이 EASY_PAY 호출 — INICIS 에선 400 가능.
      return {
        channelKey: config.PORTONE_CHANNEL_KEY,
        payMethod: 'EASY_PAY',
      };
    case 'card':
    default:
      return {
        channelKey: config.PORTONE_CHANNEL_KEY,
        payMethod: 'CARD',
      };
  }
}

// 결제 실패 vs 사용자 취소 구분 — UI 가 "취소"(info 톤)와 "실패"(error 톤)를 다르게 안내하도록
// throw 하는 Error 에 code 를 부여한다. translateApiError 가 code 없이도 fallback 으로 동작하므로
// code 는 톤 분기(variant) 용도로만 쓰인다.
const PAYMENT_CANCEL_PATTERN = /CANCEL|취소|USER_CANCEL|PAY_PROCESS_CANCELED|중단/i;
function makePaymentError(message, hint) {
  const e = new Error(message || '결제가 취소되었습니다.');
  e.code = PAYMENT_CANCEL_PATTERN.test(String(hint ?? message ?? '')) ? 'payment_cancelled' : 'payment_failed';
  return e;
}

/**
 * 전체 결제 플로우 실행.
 *
 * @param {Object} params
 * @param {number} params.reservationId
 * @param {string} params.paymentType - 'initial' | 'additional'
 * @param {string} [params.paymentMethod] - 'simple' | 'card' | 'bank' | 'paypal'
 * @param {string} [params.buyerName]
 * @param {string} [params.buyerTel]
 * @param {string} [params.buyerEmail]
 * @param {string} [params.serviceType] - 모바일 redirect 경로 구성용
 * @returns {Promise<Object>} confirm 응답 또는 vbank 발급 정보
 * @throws {Error} 결제 실패/취소 시
 */
export async function executePayment({
  reservationId,
  paymentType,
  paymentMethod = 'card',
  buyerName,
  buyerTel,
  buyerEmail,
  serviceType,
  redirectUrl,
}) {
  if (!isPortOneReady()) {
    const missing = [];
    if (!config.PORTONE_V2_STORE_ID) missing.push('STORE_ID');
    if (!getPortOne()) missing.push('SDK script');
    console.error('[PortOneV2] 결제 불가 — 미설정 항목:', missing.join(', '));
    throw new Error('결제 모듈이 준비되지 않았습니다. 관리자에게 문의해주세요.');
  }

  // PayPal은 USD로 처리해야 PortOne paypal_v2가 받아준다 → 백엔드에 환산 요청.
  const { channelKey, payMethod, currency: methodCurrency, easyPayProvider } = resolveChannel(paymentMethod);
  const requestCurrency = methodCurrency || 'KRW';

  // [DEBUG] 빌드에 박힌 채널키 검증용 — PortOne 콘솔의 실연동 채널키와 일치하는지 확인
  console.log('[PortOneV2][DEBUG] 결제 시작', {
    paymentMethod,
    payMethod,
    easyPayProvider,                             // 간편결제 5종 호출 시 지정되는 provider
    channelKey,                                  // 실제 SDK에 전달되는 값
    storeId: config.PORTONE_V2_STORE_ID,
    PAYPAL_CHANNEL_KEY: config.PORTONE_PAYPAL_CHANNEL_KEY,
    INICIS_CHANNEL_KEY: config.PORTONE_CHANNEL_KEY,
    buildMode: process.env.NODE_ENV,
  });

  // Step 1: 서버 prepare — paymentId(=order_id) 발급 + V2 pre-register 등록 (요청 통화로 환산)
  const prepareResult = await paymentService.prepare({
    reservation_id: reservationId,
    payment_type: paymentType,
    currency: requestCurrency,
  });
  // 0원(전액 할인 등) 예약: 서버가 PG 없이 무료 확정 처리 → SDK/confirm 생략하고 완료 반환.
  if (prepareResult?.free) {
    return { status: 'paid', free: true, paymentId: prepareResult.order_id };
  }
  const { order_id, amount, order_name, currency: serverCurrency } = prepareResult;
  if (!order_id || amount == null) {
    console.error('[PortOneV2] prepare 응답 부족:', prepareResult);
    throw new Error('결제 준비에 실패했습니다. 잠시 후 다시 시도해주세요.');
  }

  // Step 2: V2 SDK 결제 요청
  const PortOne = getPortOne();
  const isMobile = /Android|iPhone|iPad|iPod/i.test(navigator.userAgent);
  const phone = ((buyerTel || '').replace(/[^\d+]/g, '').replace(/^\+82/, '0').replace(/\D/g, '')).slice(0, 11);

  // PortOne V2는 totalAmount를 minor unit 정수로 받는다 (KRW=원, USD=센트).
  // 서버가 내려주는 amount는 표시용(달러). USD는 *100 후 정수로 변환해 SDK에 전달.
  // currency는 서버 응답을 source of truth로 사용 (정상 응답이면 항상 포함되어 옴).
  const finalCurrency = serverCurrency || 'KRW';
  const finalTotalAmount = finalCurrency === 'USD'
    ? Math.round(Number(amount) * 100)
    : Math.round(Number(amount));

  const requestParams = {
    storeId: config.PORTONE_V2_STORE_ID,
    channelKey,
    paymentId: order_id,
    orderName: order_name,
    totalAmount: finalTotalAmount,
    currency: finalCurrency,
    payMethod,
    customer: {
      fullName: buyerName || undefined,
      phoneNumber: phone || undefined,
      email: buyerEmail || undefined,
    },
    locale: resolveLocale(),
  };

  // 간편결제 5종: 간편결제사 명시 (easyPay 객체의 자식 속성으로 전달).
  if (easyPayProvider) {
    requestParams.easyPay = { easyPayProvider };
  }

  // 카드 결제: KG이니시스 카드창에서 간편결제(카카오·네이버·삼성·토스 등) 바로가기 버튼 제거.
  //   앱에서 이미 카드/간편결제를 분리해 각자 진입하므로 카드창은 순수 카드사 목록만 노출.
  //   PC=acceptmethod(noeasypay), 모바일=P_RESERVED(noeasypay=Y). 카드 외 분기엔 영향 없음(무회귀).
  if (payMethod === 'CARD') {
    requestParams.bypass = {
      inicis_v2: isMobile ? { P_RESERVED: ['noeasypay=Y'] } : { acceptmethod: ['noeasypay'] },
    };
  }

  // 무통장입금: 입금 기한 3일
  if (payMethod === 'VIRTUAL_ACCOUNT') {
    const deadline = new Date();
    deadline.setDate(deadline.getDate() + 3);
    requestParams.virtualAccount = {
      accountExpiry: { dueDate: deadline.toISOString() },
    };
  }

  // 모바일: 결제 후 리다이렉트 URL
  if (isMobile) {
    // 모바일 간편결제(애플페이 등)·카드는 redirect 방식 — 결제 후 이 URL 로 복귀한다.
    //   호출처가 서비스별 완료 페이지 URL(+예약 id 쿼리)을 넘긴다. redirect 시 router state 는
    //   유실되므로 예약 id 를 쿼리로 실어야 완료 페이지가 예약을 조회할 수 있다.
    requestParams.redirectUrl = redirectUrl || `${window.location.origin}/transfer/${serviceType || 'airport'}/complete`;
  }

  let response;
  try {
    response = await PortOne.requestPayment(requestParams);
  } catch (err) {
    console.error('[PortOneV2] SDK 호출 예외:', err);
    throw makePaymentError(err?.message || '결제 처리 중 오류가 발생했습니다.', err?.message);
  }

  // 결제 응답 전체 로깅은 dev 환경에서만 — 운영 콘솔에 카드/거래 정보 노출 방지.
  if (process.env.NODE_ENV !== 'production') {
    console.log('[PortOneV2] 결제 응답:', response);
  }

  // V2 응답 해석:
  //   - response가 없거나 비어있으면 명확한 실패
  //   - txId 또는 transactionId가 있으면 PG 측 거래는 발생한 것 → 백엔드 confirm으로 최종 검증
  //   - code/message 만 있고 txId 없으면 결제 자체가 진행 안 됨 (실패/취소)
  // 일부 PG(PayPal 등)는 성공이어도 부가 code(상태/안내)를 함께 내려줄 수 있어,
  // code 필드 존재만으로 실패 단정하지 않고 transactionId 유무로 판단한다.
  const hasTransaction = !!(response && (response.txId || response.transactionId));
  if (!response) {
    console.error('[PortOneV2] 결제 응답 없음');
    // 응답 자체가 없음 = 사용자가 결제창을 닫음 → 취소로 분류.
    throw makePaymentError('결제가 취소되었습니다.', '취소');
  }
  if (!hasTransaction && response.code) {
    console.error('[PortOneV2] 결제 실패 상세:', {
      code: response.code,
      message: response.message,
      paymentId: response.paymentId,
      pgCode: response.pgCode,
      pgMessage: response.pgMessage,
    });
    throw makePaymentError(
      response.message || '결제가 취소되었습니다.',
      `${response.code || ''} ${response.pgMessage || ''} ${response.message || ''}`,
    );
  }
  if (response.code) {
    // 거래는 발생했지만 SDK가 부가 code를 내림 — 디버그용 로그만 남기고 confirm으로 진행
    console.warn('[PortOneV2] 결제 응답에 code 존재(부가 정보):', { code: response.code, message: response.message });
  }

  // 무통장입금: 가상계좌 발급 성공 → confirm 없이 정보 반환 (입금 후 webhook에서 paid 전환)
  if (payMethod === 'VIRTUAL_ACCOUNT') {
    return {
      status: 'ready',
      paymentId: order_id,
      txId: response.txId,
    };
  }

  // Step 3: 서버 confirm — V2 GET /payments/{paymentId}로 검증
  const confirmResult = await paymentService.confirm({
    payment_key: response.txId, // V2 transactionId — DB 호환용으로 함께 보냄 (서버는 order_id로 조회)
    order_id,
    amount,
  });

  return confirmResult;
}
